import { Component, OnInit, ViewChild } from '@angular/core';
import Sizes from '../../../../model/product/sizes';
import { RequestService } from '../../../../services/request.service';
import { SnackService } from '../../../../services/snack.service';
import { FormatDecimal } from '../../../../pipes/format-decimal';
import { SubscriptionBasedComponent } from '../../../../subscription-helpers/subscription-based.component';
import Materials from 'src/app/model/material/materials';
import { TemporaryMaterialChoices } from 'src/app/model/product/windowsticker/windowsticker';
import ProductSize from 'src/app/model/product/product-size';
import Material from 'src/app/model/material/material';
import WindowStickerSize from 'src/app/model/product/windowsticker/windowsticker-size';
import MaterialGroup from 'src/app/model/material/material-group';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import { ModalService } from '../../../../services/modal.service';
import { PRODUCTS } from '../../../../helpers/constants';

@Component({
  selector: 'app-window-sticker-size-detail',
  templateUrl: './window-sticker-size-detail.component.html',
  styleUrls: ['./window-sticker-size-detail.component.scss'],
  providers: [FormatDecimal],
})
export class WindowStickerSizeDetailComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  size: Sizes;
  productMaterials: Materials;
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    private formatDecimal: FormatDecimal,
    private modalService: ModalService,
    private pageIndexService: PageIndexService
  ) {
    super();
  }

  ngOnInit() {
    this.size = {
      name: PRODUCTS.RAAMSTICKERS,
      url: '/window_sticker_sizes',
      productSizes: [],
    };
    this.productMaterials = {
      name: PRODUCTS.RAAMSTICKERS,
      materialGroups: this.buildWindowstickerMaterialsGroups(),
    };

    const initPageIndex = this.pageIndexService.getPageIndex(
      this.getLocalStorageKey()
    );
    const initPageSize = this.pageIndexService.getPageSize(
      this.getLocalStorageKey()
    );
    this.paginator.pageIndex = initPageIndex == null ? 0 : initPageIndex;
    this.paginator.pageSize = initPageSize == null ? 30 : initPageSize;
    this.addSubscription(
      merge(this.paginator.page).pipe(
        startWith({}),
        switchMap(() => {
          this.pageIndexService.setPageIndex(
            this.getLocalStorageKey(),
            this.paginator.pageIndex
          );
          this.pageIndexService.setPageSize(
            this.getLocalStorageKey(),
            this.paginator.pageSize
          );
          return this.requestService.get(
            this.size.url,
            null,
            this.getProductsSort(),
            this.getProductsOrder(),
            this.paginator.pageSize,
            this.paginator.pageIndex,
            null,
            [{ key: 'height', order: 'asc' }]
          );
        }),
        map((data) => {
          this.totalItems = Number.parseInt(data.headers.get('X-Total-Count'));
          return data.body;
        }),
        catchError(() => {
          return observableOf([]);
        })
      ),
      (data) => {
        this.size.productSizes = data;
        this.size.productSizes.forEach((p) => {
          p.editable = false;
        });
        this.formatPrices();
      }
    );
  }

  formatPrices() {
    this.size.productSizes.forEach((p) => {
      p.placementPrice = this.formatDecimal.transform(
        p.placementPrice,
        true,
        2
      );
      p.productionPrice = this.formatDecimal.transform(
        p.productionPrice,
        true,
        2
      );
    });
  }

  formatPrice(productSize: ProductSize) {
    productSize.placementPrice = this.formatDecimal.transform(
      productSize.placementPrice,
      true,
      2
    );
    productSize.productionPrice = this.formatDecimal.transform(
      productSize.productionPrice,
      true,
      2
    );
  }

  getProductsSort() {
    return 'materialDisplayName';
  }

  getProductsOrder() {
    return 'asc';
  }

  save(productSize: ProductSize, index) {
    const canSave = this.checkWindowstickerSize(productSize);
    if (!canSave) {
      return;
    }
    if (productSize.id != null) {
      this.addSubscription(
        this.requestService.put(productSize, this.size.url),
        (result) => {
          this.size.productSizes[index] = result;
          this.formatPrice(result);
          this.snackService.showSuccess('Prijs succesvol opgeslagen');
        }
      );
    } else {
      this.addSubscription(
        this.requestService.post(productSize, this.size.url),
        (result) => {
          this.size.productSizes[index] = result;
          this.formatPrice(result);
          this.snackService.showSuccess('Prijs succesvol opgeslagen');
        }
      );
    }
  }

  delete(productSize: ProductSize, index) {
    this.modalService.confirmDialog(
      'Product verwijderen',
      'Bent u zeker dat u deze prijs wilt verwijderen?',
      (answer) => {
        if (!answer) {
          return;
        }
        if (productSize.id == null) {
          this.size.productSizes.splice(index, 1);
          this.snackService.showSuccess('Prijs succesvol verwijderd');
        } else {
          this.addSubscription(
            this.requestService.delete(this.size.url + '/' + productSize.id),
            (result) => {
              this.size.productSizes.splice(index, 1);
              this.snackService.showSuccess('Prijs succesvol verwijderd');
            }
          );
        }
      }
    );
  }

  checkWindowstickerSize(productSize: ProductSize) {
    let size = <WindowStickerSize>productSize;
    if (size.material == null) {
      this.snackService.showError('Gelieve een materiaal te specifiëren');
      return false;
    }
    if (size.width == null) {
      this.snackService.showError('Gelieve een breedte te specifiëren');
      return false;
    }
    if (size.height == null) {
      this.snackService.showError('Gelieve een hoogte te specifiëren');
      return false;
    }
    if (size.productionPrice == null) {
      this.snackService.showError('Gelieve een aanmaakprijs te specifiëren');
      return false;
    }
    if (size.placementPrice == null) {
      this.snackService.showError('Gelieve een plaatsingsprijs te specifiëren');
      return false;
    }
    return true;
  }

  buildWindowstickerMaterialsGroups() {
    let temporaryGroup = new MaterialGroup(
      'Tijdelijk',
      TemporaryMaterialChoices.TEMPORARY.foilChoices
    );
    let permanentGroup = new MaterialGroup(
      'Permanent',
      TemporaryMaterialChoices.PERMANENT.foilChoices
    );
    return [temporaryGroup, permanentGroup];
  }

  isEditable(productSize: ProductSize) {
    return productSize.id == null;
  }

  compareMaterials(m1: Material, m2: Material) {
    if (m1 == null && m2 == null) {
      return true;
    } else if (m1 != null && m2 != null) {
      if (m1.ordinal === m2.ordinal) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  computeFormatLabel(productSize: ProductSize) {
    if (
      productSize.height == null ||
      productSize.height === 0 ||
      productSize.width == null ||
      productSize.width === 0
    ) {
      return 'TBD';
    } else if (productSize.height === productSize.width) {
      return 'Vierkant';
    } else if (productSize.height > productSize.width) {
      return 'Staand';
    } else {
      // height < width
      return 'Liggend';
    }
  }

  computeSum(productSize: ProductSize) {
    if (
      (productSize.productionPrice == null ||
        +productSize.productionPrice == 0) &&
      (productSize.placementPrice == null || +productSize.placementPrice == 0)
    ) {
      return '0,00';
    }
    if (
      productSize.productionPrice == null ||
      +productSize.productionPrice == 0
    ) {
      return productSize.placementPrice;
    }
    if (
      productSize.placementPrice == null ||
      +productSize.placementPrice == 0
    ) {
      return productSize.productionPrice;
    }
    let toFormat = (
      Number(productSize.productionPrice.replace(/,/, '.')) +
      Number(productSize.placementPrice.replace(/,/, '.'))
    ).toString();
    return this.formatDecimal.transform(toFormat, true, 2);
  }

  addProductSize() {
    const newSize: WindowStickerSize = new WindowStickerSize();
    newSize.editable = true;
    this.size.productSizes.push(newSize);
  }

  toggleEditable(productSize: ProductSize) {
    productSize.editable = !productSize.editable;
  }

  private getLocalStorageKey(): string {
    return 'WindowSizeDetailComponent';
  }

  protected readonly PRODUCTS = PRODUCTS;
}
